import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Tab, Text } from '@mercai/clever'

import { MetricCards } from '@/components'

import {
  useAIConversationList,
  useAIConversationMetrics,
  useAIDeleteConversation,
} from '@/services'

import {
  formatNumber,
  onConfirmDangerMessage,
  onErrorMessage,
  onSuccessMessage,
  useParamsState,
} from '@/helpers'

import {
  ConversationTable,
  ConversationDrawerChat,
  ConversationDrawerShow,
  ConversationDrawerNew,
  RegenerateConversation,
} from '../components'

import { IAConversationRoleEnum } from '@/types'

import styles from './development.module.css'

export const ConversationsDevelopment = () => {
  const { t } = useTranslation('screenAiCenter')
  const { t: tEnums } = useTranslation('enums')

  const [isOpenEvaluateConversation, setIsOpenEvaluateConversation] =
    useState(false)

  const [filter, setFilter] = useParamsState({
    search: '',
    page: '1',
    limit: '10',
    isNotificationOnly: 'false',
    role: '',
    rfqReference: '',
  })

  const { data: metricsData, isLoading: isLoadingMetrics } =
    useAIConversationMetrics({
      params: {
        role: filter.role,
      },
    })

  const { data, isLoading, refetch } = useAIConversationList({
    params: filter,
  })

  const { mutateAsync: mutateDelete } = useAIDeleteConversation()

  const [queryParams, setQueryParams] = useParamsState({
    uuid: '',
    show: '',
    new: '',
    form: '',
  })

  const onHandleEditRegister = (uuid: string) => {
    setQueryParams({ ...queryParams, form: 'true', show: '', new: '', uuid })
  }

  const onHandleNewRegister = () => {
    setQueryParams({
      ...queryParams,
      new: 'true',
      form: '',
      show: '',
      uuid: '',
    })
  }

  const onHandleShowRegister = (uuid: string) => {
    setQueryParams({
      ...queryParams,
      show: 'true',
      form: '',
      new: '',
      uuid,
    })
  }

  const onHandleDeleteRegister = async (uuid: string) => {
    const confirm = await onConfirmDangerMessage(
      t('conversationTableDeleteConfirmTitle'),
      t('conversationTableDeleteConfirmMessage'),
      t('conversationTableDeleteConfirmButtonDelete'),
      t('conversationTableDeleteConfirmButtonCancel'),
    )

    if (confirm) {
      const result = await mutateDelete(uuid)

      if (result.success) {
        onSuccessMessage(
          t('conversationTableDeleteSuccessTitle'),
          t('conversationTableDeleteSuccessMessage'),
        )

        refetch()
      } else {
        onErrorMessage(
          t('conversationTableDeleteErrorTitle'),
          t('conversationTableDeleteErrorMessage'),
        )
      }
    }
  }

  const onHandleRegenerateConversation = (uuid: string) => {
    setIsOpenEvaluateConversation(true)
    setQueryParams({ ...queryParams, uuid })
  }

  const getOptions = () => {
    const options = [
      {
        label: t('conversationTableTabsRoleAll'),
        value: '',
      },
    ]

    const optionsRole = Object.keys(IAConversationRoleEnum).map(
      (conversation) => ({
        label: tEnums(`conversation_role_${conversation}`),
        value: conversation,
      }),
    )

    options.push(...optionsRole)

    return options
  }

  return (
    <div className={styles.content}>
      <div className={styles.tabs}>
        <Tab
          value={filter.role || ''}
          onChange={(value) => setFilter({ ...filter, role: value })}
          items={getOptions()}
        />

        <MetricCards
          metrics={[
            {
              label: (
                <div className={styles['metrics-label']}>
                  <Text>{t('conversationTableAverageRating')}</Text>
                  <Text variant="text-xsmall">
                    {t('conversationTableMetricsLast30Days')}
                  </Text>
                </div>
              ),
              value: `${formatNumber(metricsData?.data?.generalNoteMetricsReport?.currentPeriodAvg || 0)}/5.00`,
              change:
                metricsData?.data?.generalNoteMetricsReport?.deltaPercentage,
              changeSuffix: '%',
              isLoading: isLoadingMetrics,
            },
            {
              label: (
                <div className={styles['metrics-label']}>
                  <Text>{t('conversationTableJtbdCompletedConversion')}</Text>
                  <Text variant="text-xsmall">
                    {t('conversationTableMetricsLast30Days')}
                  </Text>
                </div>
              ),
              value: `${formatNumber(metricsData?.data?.jtbdCompletedMetricsReport?.currentPeriodPercentage || 0)}%`,
              change:
                metricsData?.data?.jtbdCompletedMetricsReport?.deltaVariation,
              changeSuffix: '%',
              isLoading: isLoadingMetrics,
            },
            {
              label: (
                <div className={styles['metrics-label']}>
                  <Text>{t('conversationTableJtbdCompletedTotal')}</Text>
                  <Text variant="text-xsmall">
                    {t('conversationTableMetricsLast30Days')}
                  </Text>
                </div>
              ),
              value: `${metricsData?.data?.jtbdCompletedMetricsReport?.currentPeriodCount || 0}/${metricsData?.data?.jtbdCompletedMetricsReport?.totalCurrentPeriod || 0}`,
              change:
                metricsData?.data?.jtbdCompletedMetricsReport?.deltaPercentage,
              changeSuffix: '%',
              isLoading: isLoadingMetrics,
            },
          ]}
        />
      </div>

      <ConversationTable
        type="development"
        data={data?.data}
        isLoading={isLoading}
        filter={filter}
        onSetFilter={setFilter}
        onHandleEditRegister={onHandleEditRegister}
        onHandleRegenerateConversation={onHandleRegenerateConversation}
        onHandleNewRegister={onHandleNewRegister}
        onHandleShowRegister={onHandleShowRegister}
        onHandleDeleteRegister={onHandleDeleteRegister}
      />

      <ConversationDrawerChat
        isOpen={queryParams.form === 'true'}
        onClose={() => setQueryParams({ ...queryParams, form: '' })}
        uuid={queryParams.uuid}
      />

      <ConversationDrawerShow
        isOpen={queryParams.show === 'true'}
        onHandleOpen={() => setQueryParams({ ...queryParams, show: '' })}
        uuid={queryParams.uuid}
        variation="development"
      />

      <ConversationDrawerNew
        isOpen={queryParams.new === 'true'}
        onHandleOpen={() => setQueryParams({ ...queryParams, new: '' })}
      />

      <RegenerateConversation
        isOpen={isOpenEvaluateConversation}
        onHandleOpen={() => setIsOpenEvaluateConversation(false)}
        uuid={queryParams.uuid}
      />
    </div>
  )
}
